"use client";

import React, { useState, useEffect, useRef } from "react";
import { Link } from "@/i18n/navigation";
import { LayoutGrid, SquareArrowRight, Sparkles, Zap, ChevronDown } from "lucide-react";
import { useUser } from "@/context/UserProvider";
import { useHeaderActions } from "@/context/HeaderActionsProvider";
import UserMenu from "./UserMenu";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { ModelType } from "@/types";
import { useClickAway } from "react-use";

export default function Header({ showLocaleSwitcher = true }: { showLocaleSwitcher?: boolean }) {
  const { isLoggedIn, isLoading, credits } = useUser();
  const { actions, centerActions, label, headerClasses } = useHeaderActions();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations("Header");
  const tModel = useTranslations("ModelSelector");

  // Mobile model selector state
  const [selectedModel, setSelectedModel] = useState<ModelType>("nano-banana-pro");
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  useClickAway(modelDropdownRef, () => {
    if (isModelDropdownOpen) setIsModelDropdownOpen(false);
  }, ["mousedown", "touchstart"]);

  useEffect(() => {
    const syncModelFromStorage = () => {
      const stored = localStorage.getItem("selectedModel") as ModelType;
      if (stored) setSelectedModel(stored);
    };

    syncModelFromStorage();

    window.addEventListener("storage", syncModelFromStorage);
    window.addEventListener("selectedModelChanged", syncModelFromStorage);

    return () => {
      window.removeEventListener("storage", syncModelFromStorage);
      window.removeEventListener("selectedModelChanged", syncModelFromStorage);
    };
  }, []);

  const handleModelChange = (model: ModelType) => {
    setSelectedModel(model);
    localStorage.setItem("selectedModel", model);
    setIsModelDropdownOpen(false);
    window.dispatchEvent(new Event("selectedModelChanged"));
  };

  const toggleLocale = () => {
    const nextLocale = locale === 'ru' ? 'en' : 'ru';
    router.replace(pathname, { locale: nextLocale });
  };

  return (
    <header
      className={`fixed top-[var(--studio-vtop,0px)] w-full z-50 transition-all duration-300 border-b border-transparent material-chrome pt-[env(safe-area-inset-top)]
        h-[calc(4rem+env(safe-area-inset-top))] md:h-[calc(5rem+env(safe-area-inset-top))] overflow-visible
        ${headerClasses.join(" ")}`}
    >
      <div className="container-std h-full flex justify-between items-center">
      <div className="flex items-center gap-2 md:gap-6">
        {/* Logo - Icon only on mobile */}
        <Link href="/" className="cursor-pointer flex items-center gap-3 md:gap-4 group shrink-0">
          <div className="w-9 h-9 md:w-10 md:h-10 bg-primary/20 rounded-xl flex items-center justify-center shadow-[0_0_15px_rgba(10,132,255,0.3)] group-hover:shadow-[0_0_25px_rgba(10,132,255,0.5)] transition-all duration-300 shrink-0">
            <LayoutGrid size={18} className="text-primary md:size-[20px]" />
          </div>
          <span className="sr-only md:hidden">{t("title")}</span>
          <span className="hidden md:block text-lg md:text-2xl font-bold text-foreground tracking-widest uppercase font-sans truncate max-w-[150px] md:max-w-none">
            {t("title")}
            {label && (
              <>
                <span className="text-primary hidden lg:inline ml-2"> / </span>
                <span className="text-primary hidden lg:inline ml-2">{label.text}</span>
              </>
            )}
          </span>
        </Link>

        {/* Mobile Model Selector */}
        {isLoggedIn && (
          <div className="md:hidden relative" ref={modelDropdownRef}>
            <button
              onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-white/5 backdrop-blur-md border border-white/20 hover:border-white/40 hover:bg-white/10 active:scale-95 transition-all shadow-[0_2px_8px_rgba(0,0,0,0.2)]"
            >
              <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30">
                {selectedModel === "nano-banana-pro" ? (
                  <Zap className="w-3.5 h-3.5 text-amber-400" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                )}
              </span>
              <span className={`text-xs font-medium ${selectedModel === "nano-banana-pro" ? "text-amber-400" : "text-emerald-400"}`}>
                {selectedModel === "nano-banana-pro" ? tModel("nano_banana_pro_label") : tModel("nano_banana_label")}
              </span>
              <ChevronDown className={`w-3 h-3 text-white/60 transition-transform ${isModelDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {isModelDropdownOpen && (
              <div className="absolute left-0 top-full mt-2 w-56 bg-black/80 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl py-2 z-[80]">
                <button
                  onClick={() => handleModelChange("nano-banana")}
                  className={`w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-white/10 transition-colors ${selectedModel === "nano-banana" ? "bg-white/10" : ""}`}
                >
                  <div className={`flex items-center justify-center w-9 h-9 rounded-xl border ${selectedModel === "nano-banana" ? "bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border-emerald-500/50 text-emerald-400" : "bg-white/5 border-white/10 text-emerald-400/70"}`}>
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white">{tModel("nano_banana_label")}</div>
                    <div className="text-xs text-white/50">{tModel("nano_banana_price")}</div>
                  </div>
                  {selectedModel === "nano-banana" && <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />}
                </button>
                <div className="mx-3 my-1.5 h-px bg-white/10" />
                <button
                  onClick={() => handleModelChange("nano-banana-pro")}
                  className={`w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-white/10 transition-colors ${selectedModel === "nano-banana-pro" ? "bg-white/10" : ""}`}
                >
                  <div className={`flex items-center justify-center w-9 h-9 rounded-xl border ${selectedModel === "nano-banana-pro" ? "bg-gradient-to-br from-amber-500/30 to-orange-500/30 border-amber-500/50 text-amber-400" : "bg-white/5 border-white/10 text-amber-400/70"}`}>
                    <Zap className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white">{tModel("nano_banana_pro_label")}</div>
                    <div className="text-xs text-white/50">{tModel("nano_banana_pro_price")}</div>
                  </div>
                  {selectedModel === "nano-banana-pro" && <div className="w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.8)]" />}
                </button>
              </div>
            )}
          </div>
        )}

        {isLoggedIn && (
          <Link
            href="/billing/tokens"
            className="hidden md:flex items-center px-3 py-1 bg-secondary/50 hover:bg-secondary/70 rounded-full border border-white/10 hover:border-white/20 backdrop-blur-sm transition-all cursor-pointer"
          >
            <span className="text-xs font-bold text-muted-foreground uppercase mr-2">{t("tokens")}</span>
            <span className="text-sm font-mono font-bold text-primary">{credits}</span>
          </Link>
        )}
      </div>

      <div className="flex items-center gap-4">
        {/* Center actions (e.g., Model Selector) */}
        {centerActions.map((action) => (
          <div key={action.id}>{action.element}</div>
        ))}
        
        {showLocaleSwitcher && !isLoading && !isLoggedIn && (
          <button
            onClick={toggleLocale}
            className="w-[48px] h-[48px] flex items-center justify-center text-muted-foreground hover:text-foreground bg-secondary/50 hover:bg-secondary rounded-full transition-all border border-transparent hover:border-white/10"
            title={locale === "ru" ? "Switch to English" : "Переключить на Русский"}
            aria-label="Toggle language"
          >
            {locale.toUpperCase()}
          </button>
        )}

        {isLoading ? (
          <div className="w-[120px] h-10 bg-secondary/30 rounded-xl animate-pulse" />
        ) : !isLoggedIn ? (
          <Link href='/login'
            className="flex items-center justify-center w-[48px] h-[48px] md:px-6 md:h-[48px] md:min-w-[120px] md:rounded-xl bg-primary text-white rounded-full font-medium hover:bg-blue-600 transition-all shadow-[0_4px_14px_0_rgba(10,132,255,0.39)] hover:shadow-[0_6px_20px_rgba(10,132,255,0.23)] hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
            aria-label={t('login')}
          >
            <SquareArrowRight size={20} className="md:hidden" aria-hidden="true" />
            <span className="hidden md:inline">{t('login')}</span>
          </Link>
        ) : (
          <>
            <UserMenu />
            {actions.map((action) => (
              <div key={action.id}>{action.element}</div>
            ))}
          </>
        )}
      </div>
      </div>
    </header>
  );
}
